import { DeleteIcon, DownloadIcon, EditIcon } from "@chakra-ui/icons";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  IconButton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import CustomModal from "../../../Components/Common/CustomModal/CustomModal";
import EditAttachmentModalContent from "../../../Components/Eddss/EditAttachmentModalContent";
import FileUpload from "../../../Components/SharedComponent/FileUpload";
import { useAttachments } from "../../../hooks/useAttachments";
import { useFileValidator } from "../../../utils/fileValidation";
const AttachmentsBox = ({
  caseId,
  personId,
  attachmentInfo,
  mutateEdssData,
}) => {
  const { upload, download, edit, remove, error } = useAttachments();
  const [rowToDelete, setRowToDelete] = useState(null);
  const [rowToEdit, setRowToEdit] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { validateFiles } = useFileValidator();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const cancelRef = useRef();

  useEffect(() => {
    if (error) {
      toast.error(error.message);
    }
  }, [error]);

  const handleClickDelete = (row) => {
    setRowToDelete(row);
    onDeleteOpen();
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      const aid = rowToDelete.AID.VALUE;
      await remove(caseId, personId, aid, attachmentInfo, mutateEdssData);
      if (!error) {
        toast.success("Delete successful");
      }

      setRowToDelete(null);
      onDeleteClose();
    } finally {
      setIsDeleting(false);
    }
  };

  async function handleUpload(files) {
    setIsUploading(true);

    try {
      const { valid, errors } = await validateFiles(files);
      console.log("===check in file", valid, errors);

      if (!valid) {
        toast.error(errors.join("\n"), { autoClose: 8000 });
        return;
      }

      await upload(caseId, personId, attachmentInfo, files, mutateEdssData);
      toast.success("Upload successful");
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  }

  async function handleDownload(dataItem) {
    const aid = dataItem.AID.VALUE;
    const filename = dataItem.FILE_NAME.VALUE;
    const blobName = `${caseId}/${aid}`;
    await download("attachments", blobName, filename);
  }

  async function handleClickEdit(dataItem) {
    setRowToEdit(dataItem);
    onEditOpen();
  }

  const handleConfirmEdit = async (filename, description) => {
    const aid = rowToEdit.AID.VALUE;

    await edit(
      caseId,
      personId,
      attachmentInfo,
      aid,
      filename,
      description,
      mutateEdssData
    );
    if (!error) {
      toast.success("Edit successful");
    }

    setRowToEdit(null);
    onEditClose();
  };

  function formatDate(dateString) {
    // Expectation is yyyymmdd
    if (!/^\d{8}$/.test(dateString)) {
      return dateString;
    }

    const year = parseInt(dateString.slice(0, 4), 10);
    const month = parseInt(dateString.slice(4, 6), 10) - 1; // Month is 0-based
    const day = parseInt(dateString.slice(6, 8), 10);

    const date = new Date(year, month, day);

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  return (
    <Box>
      <Box mb={4} display="flex" gap={3}>
        <FileUpload onFileSelect={(files) => handleUpload(files)} />
        <Box width="24px" height="24px">
          {isUploading && <div className="loading-spinner" />}
        </Box>
      </Box>

      <TableContainer>
        <Table variant="simple" tableLayout="fixed">
          <Thead>
            <Tr>
              <Th>File Name</Th>
              <Th>Description</Th>
              <Th>Date Uploaded</Th>
              <Th width="200px"></Th>
            </Tr>
          </Thead>
          <Tbody>
            {attachmentInfo
              .filter((x) => x?.AID?.VALUE)
              .map((row, idx) => (
                <Tr key={idx}>
                  <Td>{row.FILE_NAME?.VALUE}</Td>
                  <Td>{row.NAME?.VALUE}</Td>
                  <Td>{formatDate(row.CREATE_DATE?.VALUE)}</Td>
                  <Td>
                    <Flex justify="flex-end" gap={2}>
                      <Tooltip label="Download" hasArrow>
                        <IconButton
                          aria-label="Download"
                          icon={<DownloadIcon />}
                          size="sm"
                          colorScheme="gray"
                          onClick={(_) => handleDownload(row)}
                        />
                      </Tooltip>
                      <Tooltip label="Edit" hasArrow>
                        <IconButton
                          aria-label="Edit"
                          icon={<EditIcon />}
                          size="sm"
                          colorScheme="gray"
                          onClick={(_) => handleClickEdit(row)}
                        />
                      </Tooltip>
                      <Tooltip label="Delete" hasArrow>
                        <IconButton
                          aria-label="Delete"
                          icon={<DeleteIcon />}
                          size="sm"
                          colorScheme="red"
                          onClick={(_) => handleClickDelete(row)}
                        />
                      </Tooltip>
                    </Flex>
                  </Td>
                </Tr>
              ))}
          </Tbody>
        </Table>
      </TableContainer>

      <CustomModal
        size="xl"
        isCentered={true}
        isOpen={isEditOpen}
        onClose={onEditClose}
        bodyContent={
          <EditAttachmentModalContent
            initialFileName={rowToEdit?.FILE_NAME.VALUE}
            initialDescription={rowToEdit?.NAME.VALUE}
            onSave={handleConfirmEdit}
          />
        }
      />

      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Deletion
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete {rowToDelete?.FILE_NAME.VALUE}?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={onDeleteClose}
                isDisabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleConfirmDelete}
                isLoading={isDeleting}
                loadingText="Deleting"
                ml={3}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default AttachmentsBox;
